import { css, cx } from "@emotion/css";
import { text, themeTokens, space, fontWeight } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
import { DeprecatedDropDownMenu, type DeprecatedDropdownMenuOption } from "./DeprecatedDropDownMenu";
const styles = {
    floatingLabelStyle: css({
        position: "absolute", // This gives us the floating label.
        fontWeight: fontWeight["400"],
        color: themeTokens.color.textField.text.secondary,
        lineHeight: "0.75rem",
        transition: "all 450ms cubic-bezier(0.23, 1, 0.32, 1) 0ms",
        fontSize: "0.75rem",
    }),
    floatingLabelErrorStyle: css({
        color: themeTokens.color.textField.text.danger,
    }),
    hintLabelStyle: css({
        position: "absolute",
        fontWeight: fontWeight["400"],
        color: themeTokens.color.textField.text.secondary,
        font: text.body.regular.large,
        top: "1.2rem",
    }),
    hintLabelDisabledStyle: css({
        color: themeTokens.color.textField.text.disabled,
    }),
    hintLabelErrorStyle: css({
        color: themeTokens.color.textField.text.danger,
    }),
    errorText: css({
        color: themeTokens.color.textField.text.danger,
        font: text.body.regular.xSmall,
        marginTop: "3px",
    }),
    container: css({
        position: "relative", // Needed for absolute positioned elements.
    }),
    containerMargin: css({
        margin: `0 0 ${space[16]} 0`, // Needed to match the bottom-spacing of our multi-selects. When you stack
        // various filters, this helps to keep them all consistent (example, see Task screen's advanced filters).
    }),
};
type Item = DeprecatedDropdownMenuOption;
export interface DeprecatedSelectFieldProps {
    floatingLabelText?: string | JSX.Element;
    errorText?: string;
    placeholder?: string;
    className?: string;
    allowClear?: boolean;
    items: DeprecatedDropdownMenuOption[];
    onCloseDropdown?: () => void;
    disabled?: boolean;
    onChange?: (value: string) => void;
    selectionRenderer?: (value: string, menuItem: Item) => React.ReactNode;
    value: string;
    empty?: string;
    filter?: React.ReactNode;
    autoFocus?: boolean;
    selectedValueAccessibleName?: string;
    children?: never;
    inlineButton?: React.ReactNode;
    /**
     * Temporary Prop from FFT to handle the differences between level 1 and level 2 page layouts.
     */
    pageLayoutV2?: boolean;
}
/**
 * @deprecated
 * Deprecated: 22/01/2026
 * Replacement: Select component from @octopusdeploy/design-system-components
 * Reason: This component is deprecated and will be removed in a future release.
 */
export class DeprecatedSelectField extends React.Component<DeprecatedSelectFieldProps> {
    static defaultProps: Partial<DeprecatedSelectFieldProps> = {
        disabled: false,
    };
    render() {
        const { allowClear, items, onCloseDropdown, disabled, onChange, selectionRenderer, value, filter, autoFocus, floatingLabelText, errorText, placeholder, selectedValueAccessibleName, className, empty, inlineButton, pageLayoutV2 } = this.props;
        const hasError = Boolean(errorText);
        return (<div className={cx(styles.container, { [styles.containerMargin]: !pageLayoutV2 }, className)} role="combobox">
                {floatingLabelText && <label className={cx(styles.floatingLabelStyle, { [styles.floatingLabelErrorStyle]: hasError })}>{floatingLabelText}</label>}
                {!value && placeholder && <div className={cx(styles.hintLabelStyle, { [styles.hintLabelDisabledStyle]: disabled, [styles.hintLabelErrorStyle]: hasError })}>{placeholder}</div>}
                <DeprecatedDropDownMenu allowClear={allowClear} disabled={disabled} value={value} selectedValueAccessibleName={selectedValueAccessibleName} onChange={onChange} selectionRenderer={selectionRenderer} filter={filter} autoFocus={autoFocus} items={items} onClose={onCloseDropdown} hasError={hasError} inlineButton={inlineButton} empty={empty}/>
                {errorText && <div className={styles.errorText}>{errorText}</div>}
            </div>);
    }
    static displayName = "DeprecatedSelectField";
}
